import UseCaseTemplate, { type UseCaseContent } from '../../components/UseCaseTemplate';

const content: UseCaseContent = {
  slug: 'parking-carbon-footprint',
  title: "Parking, Carbon Emissions, and the Indian City | Parksy",
  description:
    "Cruising for parking contributes 30%+ of urban traffic and a measurable share of city-level CO₂. Crowdsourced parking data can cut both.",
  canonicalPath: '/parking-carbon-footprint',
  ogImage: 'https://parksy.rent/og/parking-carbon-footprint.png',
  heroIntro:
    "Most parking studies focus on convenience and cost. The climate angle is rarely discussed, but it's measurable: a car cruising for 10 minutes at idle-low speeds in a dense Indian metro burns 0.15–0.25 litres of petrol and emits ~0.4 kg of CO₂. Multiply by 1 crore+ vehicles doing this twice a day, and cruising for parking contributes a meaningful share of Indian city-level CO₂. The fix is simple: a well-mapped parking supply, accessible to every driver.",
  painPoints: [
    'A 2018 IIT-Bombay study estimated 35% of Mumbai road congestion is parking-related, with a measurable CO₂ share.',
    'A typical car searching for parking in a dense Indian metro emits 0.4 kg CO₂ per search session.',
    'Indian cities collectively emit 1.5+ million tonnes of CO₂/year from parking-search traffic alone (rough estimate based on INRIX + vehicle-count data).',
    'A 1-minute reduction in parking-search time across 1 million daily vehicles saves 40,000 kg of CO₂ per day.',
  ],
  solutions: [
    { title: 'Map the supply', body: "Parksy\'s mission is to make every legal parking spot visible. The more we map, the less time drivers spend searching." },
    { title: 'Measure the impact', body: 'We plan to publish per-city emissions-saved estimates as the data grows. If you\'re a researcher, get in touch via the contact page.' },
    { title: 'Switch to public transit for short trips', body: "Parksy is for the cases where driving makes sense. For shorter trips, transit + park-and-ride is the climate-friendlier option." },
  ],
  citySpotlights: [
    { slug: 'mumbai', name: 'Mumbai', note: 'Highest density, biggest savings' },
    { slug: 'delhi', name: 'Delhi', note: 'Largest fleet impact' },
    { slug: 'bangalore', name: 'Bangalore', note: 'IT corridor traffic' },
    { slug: 'hyderabad', name: 'Hyderabad', note: 'Newer growth, growing impact' },
    { slug: 'pune', name: 'Pune', note: 'Hinjewadi effect' },
    { slug: 'chennai', name: 'Chennai', note: 'Industrial emissions' },
  ],
  faqs: [
    { question: 'How much CO₂ does cruising for parking emit?', answer: "0.4 kg CO₂ per car per search session, on average. A session averages 5–15 minutes of low-speed driving in a dense area." },
    { question: 'How much Indian city traffic is parking-related?', answer: "20–40% of urban traffic, depending on density and time of day. The 30% middle estimate is the most-cited." },
    { question: 'What\'s the cleanest way to reduce this?', answer: "Map the parking supply. Crowdsourced maps work. Singapore and San Francisco have reduced parking-search emissions by 15–25% with city-operated parking APIs." },
    { question: 'Is this a real or estimated number?', answer: "Both. The per-car CO₂ is from a measured study (RICE 2011). The city-level total is an extrapolation. We will publish a more rigorous number as Parksy data grows." },
  ],
  primaryCta: { label: 'Find parking on the map →', to: '/' },
  secondaryCta: { label: 'See traffic chaos data', to: '/traffic-chaos-india' },
  breadcrumbs: [
    { label: 'Home', to: '/' },
    { label: 'Parking carbon footprint' },
  ],
};

export default function ParkingCarbonFootprint() {
  return <UseCaseTemplate content={content} />;
}
